import type { AnalyticsEnvelope, AnalyticsEventType } from './firehoseSink';

/** The public generation proxy carries tool inputs and generated output. */
export const isFreeToolGenerationUrl = (url: string): boolean => {
  try {
    return new URL(url, 'https://qwa.invalid').pathname.replace(/\/$/, '') === '/api/quattr-content-ai/autowrite';
  } catch {
    return false;
  }
};

// These free-tool account and usage requests carry passwords, OTPs, or tokens.
// Match explicit endpoints so unrelated application requests remain recordable.
const FREE_TOOL_ACCOUNT_PATHS = new Set([
  '/api/join-content-ai/user-validations-api',
  '/api/join-content-ai/request-otp',
  '/api/join-content-ai/verify-otp',
  '/api/join-content-ai/setup-free-tools-user',
  '/api/join-content-ai/setup-tools-user',
  '/api/join-content-ai/setup-tools-user-with-google',
  '/api/join-content-ai/update-tools-users-password',
  '/api/join-content-ai/email-on-free-tools-account-creation',
  '/api/join-content-ai/free-tools-resume-reminder',
  '/api/join-content-ai/verify-day-pass-purchased',
  '/api/join-content-ai/get-user-day-pass-usage',
  '/api/join-content-ai/get-free-tools-usage',
  '/api/join-content-ai/get-paid-tools-plan-usage',
  '/api/join-content-ai/get-tool-plans-links',
]);

export const isFreeToolRecordingUrl = (url: string, pagePath?: string): boolean => {
  if (isFreeToolGenerationUrl(url)) return true;
  try {
    const path = new URL(url, 'https://qwa.invalid').pathname.replace(/\/$/, '');
    if (FREE_TOOL_ACCOUNT_PATHS.has(path)) return true;
    // Auth0's WebAuth.login sends credentials here. Restrict this rule to the
    // free-tool flow; normal application login recording is unchanged.
    return path === '/co/authenticate' && (pagePath === '/user-verify' || getFreeToolUsage({}, pagePath) !== null);
  } catch {
    return false;
  }
};

export const CONTENT_AI_TOOL_NAME = {
  anchor_text_generator: 'Anchor Text Generator',
  active_passive_voice_converter: 'Active-Passive Converter',
  cluster_keywords: 'Cluster Keywords',
  keyword_statistics: 'Keyword Statistics',
  heading_rewriter: 'Heading Rewriter',
  h1_heading_generator: 'H1 Heading Generator',
  image_alt_text_generator: 'Image Alt Text Generator',
  meta_description_generator: 'Meta Description Generator',
  paragraph_rewriter: 'Paragraph Rewriter',
  paragraph_writer: 'Paragraph Generator',
  ai_text_generator: 'AI Text Generator',
  paraphrase_text: 'Paraphraser',
  seo_friendly_url_generator: 'SEO Friendly URL Generator',
  stepwise_instructions_generator: 'Stepwise Instructions Generator',
  summary_generator: 'Summary Generator',
  discover_topics: 'Discover Topics',
  content_brief_generator: 'Content Brief Generator',
  title_generator: 'Title Generator',
  free_keyword_research_tool: 'Keyword Research Tool',
};

// Both embedded /app/tools routes and signed-in /app/ai-tools routes use these
// components. Marketing routes have a third set of slugs for the same tools.
const TOOL_SLUGS: Record<string, string> = {
  'anchor-text-generator': CONTENT_AI_TOOL_NAME.anchor_text_generator,
  'change-voice': CONTENT_AI_TOOL_NAME.active_passive_voice_converter,
  'active-passive-converter': CONTENT_AI_TOOL_NAME.active_passive_voice_converter,
  'active-passive-changer': CONTENT_AI_TOOL_NAME.active_passive_voice_converter,
  'cluster-keywords': CONTENT_AI_TOOL_NAME.cluster_keywords,
  'keyword-clustering': CONTENT_AI_TOOL_NAME.cluster_keywords,
  'get-keyword-statistics': CONTENT_AI_TOOL_NAME.keyword_statistics,
  'keyword-statistics': CONTENT_AI_TOOL_NAME.keyword_statistics,
  'heading-rewriter': CONTENT_AI_TOOL_NAME.heading_rewriter,
  'write-headings': CONTENT_AI_TOOL_NAME.h1_heading_generator,
  'h1-heading-generator': CONTENT_AI_TOOL_NAME.h1_heading_generator,
  'heading-generator': CONTENT_AI_TOOL_NAME.h1_heading_generator,
  'image-alt-text-generator': CONTENT_AI_TOOL_NAME.image_alt_text_generator,
  'meta-description-generator': CONTENT_AI_TOOL_NAME.meta_description_generator,
  'paragraph-rewriter': CONTENT_AI_TOOL_NAME.paragraph_rewriter,
  'ai-rewriter': CONTENT_AI_TOOL_NAME.paragraph_rewriter,
  'paragraph-writer': CONTENT_AI_TOOL_NAME.paragraph_writer,
  'paragraph-generator': CONTENT_AI_TOOL_NAME.paragraph_writer,
  'ai-text-generator': CONTENT_AI_TOOL_NAME.ai_text_generator,
  'paraphrase-text': CONTENT_AI_TOOL_NAME.paraphrase_text,
  paraphraser: CONTENT_AI_TOOL_NAME.paraphrase_text,
  'seo-friendly-url-generator': CONTENT_AI_TOOL_NAME.seo_friendly_url_generator,
  'seo-url-generator': CONTENT_AI_TOOL_NAME.seo_friendly_url_generator,
  'step-by-step-instructions': CONTENT_AI_TOOL_NAME.stepwise_instructions_generator,
  'step-wise-instructions-generator': CONTENT_AI_TOOL_NAME.stepwise_instructions_generator,
  'step-wise-instructions': CONTENT_AI_TOOL_NAME.stepwise_instructions_generator,
  'summerize-text': CONTENT_AI_TOOL_NAME.summary_generator,
  'summary-generator': CONTENT_AI_TOOL_NAME.summary_generator,
  'topic-discovery-tool': CONTENT_AI_TOOL_NAME.discover_topics,
  'topic-discovery': CONTENT_AI_TOOL_NAME.discover_topics,
  'discover-topics': CONTENT_AI_TOOL_NAME.discover_topics,
  'content-brief': CONTENT_AI_TOOL_NAME.content_brief_generator,
  'content-brief-generator': CONTENT_AI_TOOL_NAME.content_brief_generator,
  'write-title-tag': CONTENT_AI_TOOL_NAME.title_generator,
  'title-generator': CONTENT_AI_TOOL_NAME.title_generator,
  'public-discovery-keywords': CONTENT_AI_TOOL_NAME.free_keyword_research_tool,
  'keyword-research-tool': CONTENT_AI_TOOL_NAME.free_keyword_research_tool,
  'keyword-research': CONTENT_AI_TOOL_NAME.free_keyword_research_tool,
};

const TOOL_NAMES = new Set(Object.values(CONTENT_AI_TOOL_NAME));
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// DayPass purchases are recorded as Amplitude revenue (BuyOnePass). These are
// the only properties kept besides the tool, and only in the shapes it sends.
type FreeToolRevenue = {
  $revenue?: number;
  $price?: number;
  $currency?: string;
  $revenueType?: 'DayPass';
  $productId?: string;
  plan_id?: string;
};

export type FreeToolUsage = { tool_name: string } & FreeToolRevenue;

const PLAN_ID = /^[\w.-]{1,64}$/;

const revenueFields = (props: Record<string, unknown>): FreeToolRevenue => {
  const fields: FreeToolRevenue = {};
  if (typeof props.$revenue === 'number' && Number.isFinite(props.$revenue)) fields.$revenue = props.$revenue;
  if (typeof props.$price === 'number' && Number.isFinite(props.$price)) fields.$price = props.$price;
  if (typeof props.$currency === 'string' && /^[A-Z]{3}$/.test(props.$currency)) fields.$currency = props.$currency;
  if (props.$revenueType === 'DayPass') fields.$revenueType = 'DayPass';
  if (typeof props.$productId === 'string' && PLAN_ID.test(props.$productId)) fields.$productId = props.$productId;
  if (typeof props.plan_id === 'string' && PLAN_ID.test(props.plan_id)) fields.plan_id = props.plan_id;
  return fields;
};

type UsageSource = {
  event?: unknown;
  properties?: unknown;
  traits?: unknown;
  context?: unknown;
};

const toolRoute = (value: unknown): { scoped: boolean; tool?: string } => {
  if (typeof value !== 'string') return { scoped: false };
  try {
    const path = new URL(value, 'https://qwa.invalid').pathname;
    const match = /^\/(?:app\/(?:ai-tools|tools)|free-tools)(?:\/([^/]+))?\/?$/.exec(path);
    if (!match) return { scoped: false };
    const tool = Object.prototype.hasOwnProperty.call(TOOL_SLUGS, match[1]) ? TOOL_SLUGS[match[1]] : undefined;
    return { scoped: true, tool };
  } catch {
    return { scoped: false };
  }
};

/** Allow only a known tool label; arbitrary caller values are never copied. */
export const getFreeToolUsage = (source: UsageSource, pathname?: string): FreeToolUsage | null => {
  const props = isRecord(source.properties) ? source.properties : {};
  const traits = isRecord(source.traits) ? source.traits : {};
  const context = isRecord(source.context) ? source.context : {};
  const page = isRecord(context.page) ? context.page : {};
  const routes = [pathname, page.path, page.url, props.path, props.url, traits.path, traits.url].map(toolRoute);
  const route = routes.find((candidate) => candidate.tool);
  const namedTool = [props.tool_name, traits.tool_name].find(
    (name): name is string => typeof name === 'string' && TOOL_NAMES.has(name)
  );
  const scoped =
    routes.some((candidate) => candidate.scoped) ||
    Object.prototype.hasOwnProperty.call(props, 'tool_name') ||
    Object.prototype.hasOwnProperty.call(traits, 'tool_name') ||
    props.screen_name === 'Content AI Tools' ||
    traits.screen_name === 'Content AI Tools' ||
    (typeof source.event === 'string' && source.event.endsWith(' - Content AI Tools'));
  return scoped ? { tool_name: route?.tool || namedTool || 'Unknown free tool', ...revenueFields(props) } : null;
};

// Event names are used by existing funnels. Preserve the fixed action names,
// but do not let a caller move prompt/error text into an event name instead.
const TOOL_ACTIONS = new Set([
  'Page Viewed',
  'Free Tool Activity',
  'Button click',
  'ButtonClick - Buy Day Pass',
  'ButtonClick - Upgrade Plan',
  'Clicked - Complete Verification',
  'Clicked - Login',
  'Clicked - Request a Demo',
  'Clicked - Sign Up With Google',
  'Clicked - Submit & Get Code to Verify OR Resend code',
  'Clicked - Update my email address',
  'Clicked - privacy policy',
  'Clicked - terms of service',
  'Clicked Locked Option',
  'Clicked on Close & Continue',
  'Clicked on Continue',
  'Clicked on Cross',
  'Clicked on Go back',
  'Clicked on Go back & Continue',
  'Clicked on Go back & continue',
  'Clicked on Reset All',
  'Complete Verification with google',
  'Copy Generated Output',
  'Create a Free Account',
  'Created Account',
  'User Already Exists',
  'Shown Create Account Screen',
  'Clicked on Create Account',
  'Auto Login Success',
  'Auto Login Error',
  'Unable to Auto Login',
  'Success',
  'Error',
  'Button Click',
  'Clicked On Retry Login',
  'Clicked On Choose a Different Email',
  'Revenue tracking - DayPass Purchase',
  'Prompted DayPass and Upgrade Plan Banner',
  'Prompted Webflow Purchase Banner',
  'Failed to process the image. Please try again.',
  'Download Generated Output',
  'Encountered Error While Fetching Checkout Page Link',
  'Error creating tools account with Google',
  'Generate Output',
  'Job role question answered',
  'Job role question shown',
  'Job role question skipped',
  'Logged out due to session expiry',
  'Password Validation Error',
  'Prompted As: Something Went Wrong!',
  'Prompted As: Your Day Plan has Expired!',
  'Prompted to Sign Up',
  'Reached your maximum usage limit',
  'Redirected to Google Verify Screen',
  'Shown OTP Input field',
  'Success creating tools account in auth0',
  'Unlocked an Option',
  'Uploaded Image file',
  'Verified Email with Google',
  'Verified Email with OTP',
  'Verify Email Form Submitted',
  'Verify Email Form shown',
  'Visited Free Tool',
  'clicked on Create a Free Account',
  'clicked on Upgrade Plan',
  'error getting remaining credits',
  'signup with Google',
]);

export const safeFreeToolEventName = (event: string): string => {
  const suffix = ' - Content AI Tools';
  const action = event.endsWith(suffix) ? event.slice(0, -suffix.length) : event;
  return TOOL_ACTIONS.has(action) ? event : 'Free Tool Activity';
};

const TOOL_COUNTERS = new Set([
  'Free SEO Tools Outputs',
  'AI Content Writing Tools Outputs',
  'AI Content Optimization Outputs',
  'Keyword Research Outputs',
  'Outputs Accepted',
]);

export const isKnownFreeToolCounter = (name: string): boolean => TOOL_COUNTERS.has(name) || TOOL_ACTIONS.has(name);

type EnvelopeSource = UsageSource & {
  type?: unknown;
  timestamp?: unknown;
  originalTimestamp?: unknown;
  sentAt?: unknown;
  anonymousId?: unknown;
  userId?: unknown;
};

const validTime = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d+)?Z$/.test(value) &&
  Number.isFinite(Date.parse(value));

const eventType = (value: unknown): AnalyticsEventType => {
  switch (value) {
    case 'identify':
    case 'page':
    case 'screen':
    case 'alias':
    case 'group':
      return value;
    default:
      return 'track';
  }
};

/** Construct a new record instead of deleting a few known sensitive keys. */
export const buildFreeToolEnvelope = (source: EnvelopeSource, usage: FreeToolUsage): AnalyticsEnvelope => {
  const timestamp = validTime(source.timestamp) ? source.timestamp : new Date().toISOString();
  return {
    type: eventType(source.type),
    timestamp,
    originalTimestamp: validTime(source.originalTimestamp) ? source.originalTimestamp : timestamp,
    ...(validTime(source.sentAt) ? { sentAt: source.sentAt } : {}),
    anonymousId: typeof source.anonymousId === 'string' ? source.anonymousId : '',
    userId: typeof source.userId === 'string' ? source.userId : null,
    ...(typeof source.event === 'string' ? { event: safeFreeToolEventName(source.event) } : {}),
    properties: usage,
    context: { library: { name: 'qwa-analytics-shim', version: '1.0.0' } },
    channel: 'client',
  };
};
